// File Metadata:
// Filename: profileApi.js
// Version: 1.00
// Created: 2025-06-19
// Last Modified: 2025-06-19
// Authors: Dieuwer Descheemaecker, GitHub Copilot
// Description: RTK Query API slice for user profile management, 2FA, achievements, and activity

import { baseApi } from '../../../store/api/baseApi';

export const profileApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get current user profile
    getUserProfile: builder.query({
      query: () => '/profile',
      providesTags: ['Profile'],
      keepUnusedDataFor: 300, // 5 minutes cache for profile data
    }),
    
    // Update user profile
    updateUserProfile: builder.mutation({
      query: (profileData) => ({
        url: '/profile',
        method: 'PUT',
        body: profileData,
      }),
      invalidatesTags: ['Profile'],
      // Optimistic update
      async onQueryStarted(profileData, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          profileApi.util.updateQueryData('getUserProfile', undefined, (draft) => {
            Object.assign(draft, profileData);
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    
    // Change password
    changePassword: builder.mutation({
      query: (passwordData) => ({
        url: '/profile/change-password',
        method: 'POST',
        body: passwordData,
      }),
    }),
    
    // Get user achievements
    getUserAchievements: builder.query({
      query: () => '/profile/achievements',
      providesTags: ['Achievements'],
      keepUnusedDataFor: 600, // 10 minutes cache for achievements
    }),
    
    // Get user statistics
    getUserStatistics: builder.query({
      query: () => '/profile/statistics',
      providesTags: ['Statistics'],
      keepUnusedDataFor: 300, // 5 minutes cache for statistics
    }),
    
    // Get user activity history
    getUserActivity: builder.query({
      query: (options = {}) => {
        const {
          page = 1,
          limit = 10,
          sort = 'timestamp',
          order = 'desc',
          action,
          entityType,
          startDate,
          endDate,
          search,
        } = options;
        
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
          sort,
          order,
        });
        
        if (action) params.append('action', action);
        if (entityType) params.append('entityType', entityType);
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        if (search) params.append('search', search);
        
        return `/profile/activity?${params.toString()}`;
      },
      providesTags: ['Activity'],
      keepUnusedDataFor: 120, // 2 minutes cache for activity
    }),
    
    // Two-Factor Authentication endpoints
    
    // Setup 2FA (get QR code)
    setupTwoFactorAuth: builder.mutation({
      query: () => ({
        url: '/auth/2fa/setup',
        method: 'POST',
      }),
    }),
    
    // Verify 2FA code (enable 2FA)
    verifyTwoFactorAuth: builder.mutation({
      query: ({ code }) => ({
        url: '/auth/2fa/verify',
        method: 'POST',
        body: { code },
      }),
      invalidatesTags: ['Profile'], // Refresh profile to show 2FA status
    }),
    
    // Disable 2FA
    disableTwoFactorAuth: builder.mutation({
      query: ({ code }) => ({
        url: '/auth/2fa/disable',
        method: 'POST',
        body: { code },
      }),
      invalidatesTags: ['Profile'], // Refresh profile to show 2FA status
    }),
    
    // Update user preferences
    updateUserPreferences: builder.mutation({
      query: (preferences) => ({
        url: '/profile/preferences',
        method: 'PUT',
        body: preferences,
      }),
      invalidatesTags: ['Profile'],
      // Optimistic update
      async onQueryStarted(preferences, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          profileApi.util.updateQueryData('getUserProfile', undefined, (draft) => {
            draft.preferences = { ...draft.preferences, ...preferences };
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    
    // Update user points
    updateUserPoints: builder.mutation({
      query: ({ points, reason }) => ({
        url: '/profile/points',
        method: 'POST',
        body: { points, reason },
      }),
      invalidatesTags: ['Profile', 'Statistics'],
      // Optimistic update
      async onQueryStarted({ points }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          profileApi.util.updateQueryData('getUserProfile', undefined, (draft) => {
            draft.points = (draft.points || 0) + points;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    
    // Update user avatar
    updateUserAvatar: builder.mutation({
      query: (formData) => ({
        url: '/profile/avatar',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Profile'],
      // Optimistic update (note: for file uploads, we typically don't do optimistic updates)
    }),
    
    // Delete user avatar
    deleteUserAvatar: builder.mutation({
      query: () => ({
        url: '/profile/avatar',
        method: 'DELETE',
      }),
      invalidatesTags: ['Profile'],
      // Optimistic update
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          profileApi.util.updateQueryData('getUserProfile', undefined, (draft) => {
            draft.avatar = null;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    
    // Get profile analytics
    getProfileAnalytics: builder.query({
      query: ({ period = '30d' } = {}) => `/profile/analytics?period=${period}`,
      providesTags: ['Analytics'],
      keepUnusedDataFor: 300, // 5 minutes cache for analytics
    }),
    
    // Export user data (GDPR compliance)
    exportUserData: builder.mutation({
      query: () => ({
        url: '/profile/export',
        method: 'POST',
      }),
    }),
    
    // Delete user account
    deleteUserAccount: builder.mutation({
      query: ({ password, confirmText }) => ({
        url: '/profile/delete',
        method: 'DELETE',
        body: { password, confirmText },
      }),
    }),
  }),
});

export const {
  useGetUserProfileQuery,
  useUpdateUserProfileMutation,
  useChangePasswordMutation,
  useGetUserAchievementsQuery,
  useGetUserStatisticsQuery,
  useGetUserActivityQuery,
  useSetupTwoFactorAuthMutation,
  useVerifyTwoFactorAuthMutation,
  useDisableTwoFactorAuthMutation,
  useUpdateUserPreferencesMutation,
  useUpdateUserPointsMutation,
  useUpdateUserAvatarMutation,
  useDeleteUserAvatarMutation,
  useGetProfileAnalyticsQuery,
  useExportUserDataMutation,
  useDeleteUserAccountMutation,
} = profileApi;
